import { API_BASE } from '@/shared/services/api';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Textarea } from '@/shared/components/ui/textarea';
import { Label } from '@/shared/components/ui/label';
import PhotoCapture from '../components/PhotoCapture';
import {
  XCircle,
  MapPin,
  Package,
  Camera,
  FileText,
  AlertTriangle,
  Clock,
  UserX,
  Home,
  Ban,
  HelpCircle,
  Lock,
  Cloud,
  Truck,
  MoreHorizontal,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { useDriver } from '../context/DriverContext';
import { parcelEvents } from '@/shared/services/trailService';

const API_URL = API_BASE;

// Icon mapping for dynamically loaded icons
const ICON_MAP = {
  UserX, Home, Ban, AlertTriangle, Clock, HelpCircle, MapPin, Lock, Cloud, Truck, MoreHorizontal
};

const StopFailed = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeRoute, currentStop, markDeliveryFailed, geoLocation, queuePOD } = useDriver();
  
  const [selectedReason, setSelectedReason] = useState(null);
  const [notes, setNotes] = useState('');
  const [photo, setPhoto] = useState(null);
  const [failureReasons, setFailureReasons] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch failure reasons from API
  useEffect(() => {
    const fetchFailureReasons = async () => {
      try {
        const response = await fetch(`${API_URL}/api/reference/failure-reasons`);
        if (response.ok) {
          const data = await response.json();
          // Map icon strings to actual icon components
          const mappedData = data.map(reason => ({
            id: reason.code,
            label: reason.label,
            icon: ICON_MAP[reason.icon] || HelpCircle,
            requiresNote: reason.requires_note,
            requiresPhoto: reason.requires_photo
          }));
          setFailureReasons(mappedData);
        }
      } catch (error) {
        console.error('Failed to fetch failure reasons:', error);
        // Fallback to minimal list
        setFailureReasons([
          { id: 'NOT_HOME', label: 'Customer Not Home', icon: UserX },
          { id: 'OTHER', label: 'Other Reason', icon: HelpCircle }
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchFailureReasons();
  }, []);

  // Get stop from context or navigation state
  const stopId = location.state?.stopId;
  const stop = currentStop || activeRoute?.stops?.find(s => s.id === stopId);

  if (!stop) {
    return (
      <div className="p-4 text-center py-12">
        <XCircle className="h-12 w-12 mx-auto text-gray-400 mb-4" />
        <h2 className="text-xl font-semibold mb-2">No Stop Selected</h2>
        <Button onClick={() => navigate('/driver/route-active')}>Back to Route</Button>
      </div>
    );
  }

  const packages = stop.packages || [];
  const selectedReasonObj = failureReasons.find(r => r.id === selectedReason);
  const requiresNote = selectedReasonObj?.requiresNote || selectedReason === 'OTHER';
  const canSubmit = selectedReason && (!requiresNote || notes.trim());

  const handleSubmitFailure = async () => {
    const driverId = (JSON.parse(localStorage.getItem('ezerpUser') || '{}')?.id || '') || 'driver';
    
    const failureData = {
      stopId: stop.id,
      reason: selectedReason,
      reasonLabel: failureReasons.find(r => r.id === selectedReason)?.label,
      notes: notes.trim(),
      photo,
      packageIds: packages.map(p => p.id),
      failedAt: new Date().toISOString(),
      geoStamp: geoLocation
    };

    // Emit trail event for failed delivery
    await parcelEvents.stopFailed(
      activeRoute?.id,
      stop.id,
      selectedReason,
      notes.trim() || failureReasons.find(r => r.id === selectedReason)?.label,
      [], // contact attempts
      driverId
    );

    markDeliveryFailed(stop.id, failureData);
    
    // Queue for sync if there's evidence
    if (photo) {
      queuePOD({
        type: 'FAILURE_EVIDENCE',
        ...failureData
      });
    }

    toast.error('Delivery Failed', { 
      description: `${packages.length} packages marked as undeliverable` 
    });
    navigate('/driver/route-active');
  };

  return (
    <div className="p-4 space-y-4" data-testid="stop-failed">
      {/* Stop Header */}
      <Card className="bg-gradient-to-r from-red-500 to-red-600 text-white">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-white/20">
              <XCircle className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h2 className="font-semibold text-lg">Failed Delivery</h2>
              <p className="text-sm opacity-90">{stop.customer}</p>
              <p className="text-xs opacity-75">{stop.address}</p>
              <Badge className="mt-2 bg-white/20 text-white">
                <Package className="h-3 w-3 mr-1" />
                {packages.length} packages
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Failure Reason Selection */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            Select Reason
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : failureReasons.map((reason) => {
            const Icon = reason.icon;
            const isSelected = selectedReason === reason.id;
            return (
              <Button
                key={reason.id}
                variant={isSelected ? 'default' : 'outline'}
                className={`w-full justify-start gap-3 ${
                  isSelected ? 'bg-red-500 hover:bg-red-600' : ''
                }`}
                onClick={() => setSelectedReason(reason.id)}
                data-testid={`reason-${reason.id}`}
              >
                <Icon className="h-4 w-4" />
                {reason.label}
              </Button>
            );
          })}
        </CardContent>
      </Card>

      {/* Notes (required for OTHER) */}
      {selectedReason && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Notes {selectedReason === 'OTHER' && <span className="text-red-500">*</span>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Add any additional details..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="min-h-20"
              data-testid="failure-notes"
            />
          </CardContent>
        </Card>
      )}

      {/* Photo Evidence (optional but recommended) */}
      {selectedReason && (
        <PhotoCapture
          title="Evidence Photo (Optional)"
          subtitle="Take a photo to document the failed delivery attempt"
          onCapture={setPhoto}
          onClear={() => setPhoto(null)}
        />
      )}

      {/* Submit Button */}
      <div className="space-y-3">
        <Button 
          onClick={handleSubmitFailure}
          disabled={!canSubmit}
          className="w-full bg-red-500 hover:bg-red-600"
          size="lg"
          data-testid="submit-failure-btn"
        >
          <XCircle className="h-5 w-5 mr-2" />
          Confirm Failed Delivery
        </Button>

        <Button 
          variant="outline"
          onClick={() => navigate('/driver/stop-deliver', { state: { stopId: stop.id } })}
          className="w-full"
        >
          Cancel - Return to Delivery
        </Button>
      </div>

      {/* GPS Status */}
      {geoLocation && (
        <p className="text-center text-xs text-muted-foreground">
          Location recorded: {(geoLocation.lat ?? 0).toFixed(4)}, {(geoLocation.lng ?? 0).toFixed(4)}
        </p>
      )}
    </div>
  );
};

export default StopFailed;
